import { useEffect, useRef, useState } from "react";
import { Bell, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function NotificationBell() {
  const [leads, setLeads] = useState([]);
  const [seenAt, setSeenAt] = useState(() => localStorage.getItem("leads_seen_at") || "");
  const prevNewest = useRef(null);

  const fetchLeads = async () => {
    try {
      const r = await api.get("/leads");
      const data = r.data || [];
      setLeads(data);
      const newest = data[0]?.created_at || null;
      if (prevNewest.current && newest && newest > prevNewest.current) {
        const latest = data[0];
        toast.success(`Lead baru: ${latest.name}`, { description: `${latest.business_name} · ${latest.whatsapp}` });
      }
      prevNewest.current = newest;
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    fetchLeads();
    const t = setInterval(fetchLeads, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const unseen = leads.filter((l) => !seenAt || l.created_at > seenAt);

  const markSeen = (open) => {
    if (open && leads.length) {
      const newest = leads[0].created_at;
      localStorage.setItem("leads_seen_at", newest);
      setSeenAt(newest);
    }
  };

  const waLink = (num) => `https://wa.me/${(num || "").replace(/[^0-9]/g, "").replace(/^0/, "62")}`;

  return (
    <DropdownMenu onOpenChange={markSeen}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notification-bell" className="relative shrink-0 w-11 h-11 rounded-xl bg-[#FFFFFF] border border-[#E2E8F0] hover:border-[#3B82F6] flex items-center justify-center transition-colors">
          <Bell size={18} className="text-slate-700" />
          {unseen.length > 0 && (
            <span data-testid="notification-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F43F5E] text-white text-[10px] font-bold flex items-center justify-center">
              {unseen.length > 9 ? "9+" : unseen.length}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 w-80 max-w-[calc(100vw-2rem)] p-0">
        <div className="px-4 py-3 border-b border-[#E2E8F0] flex items-center justify-between">
          <span className="font-heading font-bold text-sm">Notifikasi Lead</span>
          <span className="text-xs text-slate-500">{unseen.length} baru</span>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {leads.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">Belum ada lead.</div>
          ) : (
            leads.slice(0, 10).map((l) => {
              const isNew = !seenAt || l.created_at > seenAt;
              return (
                <a key={l.id} href={waLink(l.whatsapp)} target="_blank" rel="noreferrer" data-testid={`notification-item-${l.id}`} className={`flex items-start gap-3 px-4 py-3 border-b border-[#F1F5F9] hover:bg-[#F1F5F9] transition-colors ${isNew ? "bg-[#F1F5F9]/40" : ""}`}>
                  <div className="w-8 h-8 rounded-full bg-[#2563EB]/20 flex items-center justify-center shrink-0">
                    <MessageCircle size={15} className="text-[#2563EB]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate flex items-center gap-1">
                      {l.name}{isNew && <span className="w-1.5 h-1.5 rounded-full bg-[#F43F5E]" />}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{l.business_name} · {l.whatsapp}</p>
                    <p className="text-[11px] text-slate-600">{new Date(l.created_at).toLocaleString("id-ID")}</p>
                  </div>
                </a>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
